import { useCallback, useEffect, useMemo } from 'react'
import maplibregl from 'maplibre-gl'
import debounce from 'lodash/debounce'
import { EntityLocationDTO } from '@user/store/map/typings'

type GeoPoint = { lat: number; lng: number }

type Props = {
  map: maplibregl.Map | null
  // Signature matches refreshMapPins — GeoPoint is structurally compatible with H.geo.Point
  onViewChange?: (
    forceUpdate: boolean,
    topLeft?: GeoPoint,
    bottomRight?: GeoPoint,
    center?: GeoPoint,
    zoom?: number,
  ) => void
  handleAddLeadSaveSuccess?: (newPin: EntityLocationDTO) => void
}

function storeMapStateToLocalStorage(center?: GeoPoint, zoom?: number) {
  if (center) {
    localStorage.setItem(
      'mapCenter',
      JSON.stringify({ lat: center.lat, lng: center.lng }),
    )
  }
  if (typeof zoom === 'number') {
    localStorage.setItem('mapZoom', zoom.toString())
  }
}

export function useMapLibreEvents({
  map,
  onViewChange,
  handleAddLeadSaveSuccess,
}: Props) {
  const debounceDelay = Number(process.env.REACT_APP_MAP_DEBOUNCE_DELAY) || 500

  const resetMapState = useCallback(
    (forceUpdate: boolean) => {
      if (!map) return
      const bounds = map.getBounds()
      const topLeft: GeoPoint = {
        lat: bounds.getNorth(),
        lng: bounds.getWest(),
      }
      const bottomRight: GeoPoint = {
        lat: bounds.getSouth(),
        lng: bounds.getEast(),
      }
      const c = map.getCenter()
      const center: GeoPoint = { lat: c.lat, lng: c.lng }
      const zoom = map.getZoom()
      storeMapStateToLocalStorage(center, zoom)
      onViewChange?.(forceUpdate, topLeft, bottomRight, center, zoom)
    },
    [map, onViewChange],
  )

  const debouncedOnViewChange = useMemo(
    () => debounce(() => resetMapState(false), debounceDelay),
    [debounceDelay, resetMapState],
  )

  const handleMapAfterAddLeadSaveSuccess = useCallback(
    (newPin: EntityLocationDTO) => {
      resetMapState(true)
      handleAddLeadSaveSuccess?.(newPin)
    },
    [handleAddLeadSaveSuccess, resetMapState],
  )

  useEffect(() => {
    if (!map) return
    map.on('moveend', debouncedOnViewChange)
    window.addEventListener('resize', debouncedOnViewChange)
    debouncedOnViewChange()
    return () => {
      map.off('moveend', debouncedOnViewChange)
      window.removeEventListener('resize', debouncedOnViewChange)
      debouncedOnViewChange.cancel()
    }
  }, [map, debouncedOnViewChange])

  const manualRefresh = useCallback(() => resetMapState(true), [resetMapState])

  return { handleMapAfterAddLeadSaveSuccess, manualRefresh }
}
